<!-- 查看商品：{title} -->
<template>
  <!-- 查看商品：{title} -->
  <article
    v-if="item.goodsCode"
    class="mobile-goods-list-item"
    role="button"
    tabindex="0"
    :aria-label="$t('marketComponents.viewProduct', { title: displayTitle })"
    @click="$emit('select', item)"
    @keyup.enter="$emit('select', item)"
  >
    <img
      class="mobile-goods-list-item__image"
      :src="item.goodsImage || defaultImage"
      :alt="displayTitle"
      loading="lazy"
      @error="handleImageError"
    />
    <div class="mobile-goods-list-item__content">
      <div class="mobile-goods-list-item__identity">
        <span v-for="field in identityFields" :key="field.label">
          <small>{{ field.label }}</small>
          <strong :title="field.value">{{ displayValue(field.value) }}</strong>
        </span>
      </div>

      <div class="mobile-goods-list-item__fields">
        <span v-for="field in specificationFields" :key="field.label">
          <small>{{ field.label }}</small>
          <em :title="field.value">{{ displayValue(field.value) }}</em>
        </span>
      </div>

      <div class="mobile-goods-list-item__footer">
        <span class="price">
          <!-- 价格 -->
          <small>{{ $t("marketComponents.price") }}</small>
          <strong>{{ priceText }}</strong>
        </span>
        <span>
          <!-- 数量 -->
          <small>{{ $t("marketComponents.quantity") }}</small>
          {{ displayValue(quantityText) }}
        </span>
        <span v-if="!publisherChat" class="publisher">
          <!-- 发布人 -->
          <small>{{ $t("marketComponents.publisher") }}</small>
          {{ displayValue(item.shopContacts) }}
        </span>
        <PublisherChatAvatar v-else class="publisher-chat" :name="item.shopContacts" :avatar="item.shopContactsImage" :user-id="item.shopContactsId" :available="Boolean(item.goodsCode && item.shopContacts)" @chat="$emit('publisher-chat', item)" />
      </div>
    </div>
  </article>
</template>

<script>
import { mapState } from "vuex";
import PublisherChatAvatar from '@/components/chat-workbench/PublisherChatAvatar.vue';

export default {
  name: "HomeGoodsListItem",
  components: { PublisherChatAvatar },
  props: {
    publisherChat: Boolean,
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      defaultImage: require("@/assets/default-goods.png"),
    };
  },
  computed: {
    ...mapState({
      priceUnitCNY: (state) => state.price.priceUnitCNY,
      priceUnitUSD: (state) => state.price.priceUnitUSD,
    }),
    displayTitle() {
      return (
        this.item.goodsName ||
        this.item.title ||
        this.item.goodsNum ||
        // 未命名商品
        this.$t("marketComponents.unnamedProduct")
      );
    },
    identityFields() {
      return [
        {
          // 品牌
          label: this.$t("marketComponents.brand"),
          value: this.item.goodsBrand,
        },
        {
          // 料号
          label: this.$t("marketComponents.partNumber"),
          value: this.item.goodsNum,
        },
      ];
    },
    specificationFields() {
      return [
        {
          // 类型
          label: this.$t("marketComponents.type"),
          value: this.item.productType,
        },
        {
          // 容量
          label: this.$t("marketComponents.capacity"),
          value: this.item.capacity,
        },
        {
          // 架构
          label: this.$t("marketComponents.architecture"),
          value: this.item.architecture,
        },
        // 速率
        { label: this.$t("marketComponents.speed"), value: this.item.speed },
        // 球位
        { label: this.$t("marketComponents.ballCount"), value: this.item.ball },
        { label: "DC", value: this.item.manufactureDate },
      ];
    },
    unitText() {
      const fallback = Number(this.item.priceUnit) === 10 ? "USD" : "CNY";
      const unit =
        Number(this.item.priceUnit) === 10
          ? this.priceUnitUSD
          : this.priceUnitCNY;
      return unit?.name || unit?.remark || fallback;
    },
    priceText() {
      if (
        this.item.salePrice === undefined ||
        this.item.salePrice === null ||
        this.item.salePrice === ""
      ) {
        // 价格面议
        return this.$t("marketComponents.priceOnRequest");
      }
      return `${this.unitText} ${this.$formatPrice(this.item.salePrice)}`;
    },
    quantityText() {
      const quantity = this.item.quantity ?? this.item.goodsQuantity;
      if (quantity === undefined || quantity === null || quantity === "") {
        return "";
      }
      return `${quantity}${
        this.item.goodsUnit ? ` ${this.item.goodsUnit}` : ""
      }`;
    },
  },
  methods: {
    displayValue(value) {
      return value === undefined || value === null || value === ""
        ? "--"
        : value;
    },
    handleImageError(event) {
      if (event.target.dataset.fallbackApplied) return;
      event.target.dataset.fallbackApplied = "true";
      event.target.src = this.defaultImage;
    },
  },
};
</script>

<style lang="less" scoped>
.mobile-goods-list-item {
  .publisher-chat { margin-left: auto; }
  display: flex;
  box-sizing: border-box;
  width: 100%;
  gap: 9px;
  padding: 9px;
  background: #ffffff;
  border: 1px solid #e9edf3;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(30, 56, 96, 0.04);
  outline: none;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: #f8faff;
  }
}

.mobile-goods-list-item__image {
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  object-fit: cover;
  background: #f3f5f8;
  border-radius: 8px;
}

.mobile-goods-list-item__content {
  flex: 1;
  min-width: 0;
  font-size: 10px;
  line-height: 16px;
}

.mobile-goods-list-item__identity {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 8px;

  span {
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: 4px;
  }

  small {
    flex: 0 0 auto;
    color: #98a2b3;
    font-size: 10px;
    line-height: 17px;
  }

  strong {
    min-width: 0;
    overflow: hidden;
    color: #252b33;
    font-size: 10px;
    font-weight: 600;
    line-height: 17px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.mobile-goods-list-item__fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2px 6px;
  margin-top: 4px;

  span {
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: 3px;
    color: #475467;
    font-size: 10px;
    line-height: 15px;
  }

  em {
    min-width: 0;
    overflow: hidden;
    font-style: normal;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    flex: 0 0 auto;
    color: #98a2b3;
    font-size: 10px;
  }
}

.mobile-goods-list-item__footer {
  display: flex;
  align-items: center;
  gap: 7px;
  padding-top: 4px;
  margin-top: 4px;
  color: #667085;
  font-size: 10px;
  line-height: 16px;
  border-top: 1px solid #f0f2f5;

  > span {
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: 3px;
    white-space: nowrap;
  }

  small {
    color: #98a2b3;
    font-size: 10px;
  }

  strong {
    color: #4286f5;
    font-size: 10px;
    font-weight: 700;
  }

  .publisher {
    margin-left: auto;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
